import React, { useState, useEffect } from "react";
import * as PR from "../../prime-modules/index";
import "./email-manager.scss";

const EmailManagerModal = ({ visible, onClose, initialEmails = [], onSave, invalidEmails = [] }) => {
    const [emails, setEmails] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (visible) {
            setEmails(initialEmails || []);
            setInputValue("");
            setError("");
        }
    }, [visible, initialEmails]);

    const validateEmail = (email) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email.trim());
    };

    const handleAddEmail = () => {
        if (!inputValue.trim()) {
            return;
        }

        const emailsToAdd = inputValue
            .split(/[,;\s]+/)
            .map((e) => e.trim())
            .filter((e) => e !== "");

        const newEmails = [...emails];
        const invalidEmails = [];
        let skippedCount = 0;

        emailsToAdd.forEach((email) => {
            if (!validateEmail(email)) {
                invalidEmails.push(email);
            } else if (!newEmails.includes(email)) {
                if (newEmails.length < 200) {
                    newEmails.push(email);
                } else {
                    skippedCount++;
                }
            }
        });

        // Build error message
        let errorMessage = "";
        if (invalidEmails.length > 0) {
            errorMessage = `Invalid email(s): ${invalidEmails.join(", ")}`;
        }

        if (skippedCount > 0) {
            const skipMessage = `Cannot add ${skippedCount} email(s). Maximum limit of 200 emails reached.`;
            errorMessage = errorMessage ? `${errorMessage} | ${skipMessage}` : skipMessage;
        }

        if (newEmails.length >= 200 && skippedCount === 0 && invalidEmails.length === 0) {
            errorMessage = "Maximum limit of 200 emails reached. Cannot add more emails.";
        }

        setError(errorMessage);
        setEmails(newEmails);
        setInputValue("");

        // Auto-validate/search after adding emails
        onSave(newEmails, false);
    };

    const handleRemoveEmail = (emailToRemove) => {
        setEmails(emails.filter((email) => email !== emailToRemove));
        setError("");
    };

    const handleSave = () => {
        const validEmails = emails.filter(email => validateEmail(email));

        if (validEmails.length === 0) {
            setError("Please add at least one valid email");
            return;
        }

        const invalidCount = emails.length - validEmails.length;
        if (invalidCount > 0) {
            setError(`Removing ${invalidCount} invalid email(s)`);
            setEmails(validEmails);
            return;
        }

        onSave(validEmails);
        handleClose();
    };

    const handleClose = () => {
        setInputValue("");
        setError("");
        onClose();
    };

    const handleClearAll = () => {
        setEmails([]);
        setInputValue("");
        setError("");
    };

    const handleRemoveAllInvalid = () => {
        const remainingEmails = emails.filter(email => !invalidEmails.includes(email));
        setEmails(remainingEmails);
        onSave(remainingEmails, false);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddEmail();
        }
    };

    return (
        <PR.Dialog
            header="Manage Customer Emails"
            visible={visible}
            onHide={handleClose}
            draggable={false}
            blockScroll={true}
            resizable={false}
            dismissableMask={false}
            className="email-manager-dialog"
            style={{ width: "700px" }}
        >
            <div className="email-manager-content">
                <div className="email-input-section">
                    <div className="flex align-items-center justify-content-between mb-3">
                        <label className="font-semibold text-base">
                            Add Customer Emails
                        </label>
                        <span className="text-sm text-gray-600">
                            {emails.length} / 200 emails
                        </span>
                    </div>

                    <div className="flex gap-2 mb-2">
                        <PR.InputTextarea
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Enter email addresses (separated by comma, semicolon, or space)"
                            rows={4}
                            className="flex-1"
                            style={{ maxHeight: "150px", overflowY: "auto" }}
                        />
                    </div>

                    <div className="flex gap-2 mb-3">
                        <PR.Button
                            label="Add Emails"
                            icon="pi pi-plus"
                            onClick={handleAddEmail}
                            className="email-buttons small"
                            disabled={!inputValue.trim() || emails.length >= 200}
                        />
                        <PR.Button
                            label="Clear All"
                            icon="pi pi-trash"
                            onClick={handleClearAll}
                            className="email-buttons small colorred"
                            disabled={emails.length === 0}
                        />
                    </div>

                    {error && (
                        <div className="error-message mb-3">
                            <i className="pi pi-exclamation-circle mr-2" />
                            {error}
                        </div>
                    )}
                </div>

                <div className="email-chips-section">
                    <div className="flex align-items-center justify-content-between mb-2">
                        <label className="font-semibold text-base">
                            Selected Emails
                        </label>
                        {invalidEmails.length > 0 && (
                            <PR.Button
                                label="Remove All Invalid"
                                icon="pi pi-filter-slash"
                                onClick={handleRemoveAllInvalid}
                                className="email-buttons small"
                                style={{ backgroundColor: "#e74c3c" }}
                            />
                        )}
                    </div>

                    {emails.length === 0 ? (
                        <div className="empty-state">
                            <i className="pi pi-inbox" style={{ fontSize: "2rem", color: "#ccc" }} />
                            <p className="text-gray-500 mt-2">No emails added yet</p>
                        </div>
                    ) : (
                        <div className="email-list-grid">
                            {emails.map((email, index) => (
                                <div key={index} className={`email-chip-item ${invalidEmails.includes(email) ? "is-invalid" : ""}`}>
                                    <span className="email-text" title={email}>
                                        {email}
                                    </span>
                                    <button
                                        className="remove-email-btn"
                                        onClick={() => handleRemoveEmail(email)}
                                        type="button"
                                    >
                                        <i className="pi pi-times" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="email-manager-footer">
                    <PR.Button
                        label="Cancel"
                        className="email-buttons colorgrey"
                        onClick={handleClose}
                    />
                    <PR.Button
                        label={`Save ${emails.length} Email${emails.length !== 1 ? 's' : ''}`}
                        onClick={handleSave}
                        disabled={emails.length === 0 || invalidEmails.length > 0}
                        className="email-buttons"
                    />
                </div>
            </div>
        </PR.Dialog>
    );
};

export default EmailManagerModal;
